import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useContactsApi } from 'src/modules/contacts/api';
import { useInboxApi, INBOX_CONTACTS_SEARCH_SCOPE } from '../api';
import { cleanPhoneInput, isValidPhoneNumber, normalizePhoneNumber } from '../helpers';
import { ContactsSearchContact, ContactsSearchProps } from '../types';

const PAGE_LIMIT = 30;
const SEARCH_DEBOUNCE_MS = 500;

const isRequestCanceled = (error: unknown) =>
  axios.isAxiosError(error) && error.code === 'ERR_CANCELED';

export const useContactsSearchController = ({
  onSelectContact,
  setIsChatLoading,
  group,
  refreshContacts,
}: ContactsSearchProps) => {
  const searchInputRef = useRef<HTMLDivElement | HTMLTextAreaElement>(null);
  const creatingContactRef = useRef<boolean>(false);
  const selectedCompanyId: string | undefined = useSelector(getCurrentCompanyId);
  const [hideHeader, setHideHeader] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [contacts, setContacts] = useState<ContactsSearchContact[]>();
  const [isContactsLoading, setIsContactsLoading] = useState<boolean>(false);
  const [isCreatingContact, setIsCreatingContact] = useState<boolean>(false);
  const [shouldShowDropdown, setShouldShowDropdown] = useState<boolean>(false);
  const [showConsentModal, setShowConsentModal] = useState<boolean>(false);
  const [pendingContactPhone, setPendingContactPhone] = useState<string>('');
  const { getContacts: getAllContacts } = useInboxApi();
  const { createContacts } = useContactsApi();

  const showConsentModalForContact = (phoneInput: string) => {
    // Double protection against duplicate calls
    if (isCreatingContact || creatingContactRef.current || showConsentModal) {
      return;
    }

    setShouldShowDropdown(false);
    const cleanPhone = cleanPhoneInput(phoneInput).replace(/^\+1/, '').trim();
    const phoneNumber = '+1' + normalizePhoneNumber(cleanPhone);
    setPendingContactPhone(phoneNumber);
    setShowConsentModal(true);
  };

  const createNewContact = () => {
    // Double protection against duplicate calls
    if (isCreatingContact || creatingContactRef.current) {
      return;
    }

    setIsCreatingContact(true);
    creatingContactRef.current = true;
    setShowConsentModal(false);
    setIsChatLoading(true);

    const newContact = {
      email: '',
      firstName: '',
      lastName: '',
      birthdate: '',
      timeZone: '',
      phoneNumber: pendingContactPhone,
    };

    createContacts(selectedCompanyId!, [newContact])
      .then(data => {
        setIsChatLoading(true);
        refreshContacts(() => {
          onSelectContact(data.data[0].id, data.data[0].phoneNumber);
          setIsCreatingContact(false);
          creatingContactRef.current = false;
          setHideHeader(true);
          setPendingContactPhone('');
        });
      })
      .catch(error => {
        console.error('Error creating contact:', error);
        setIsCreatingContact(false);
        creatingContactRef.current = false;
        setIsChatLoading(false);
        setShouldShowDropdown(search.length > 0);
        setPendingContactPhone('');
      });
  };

  const handleConsentModalClose = () => {
    setShowConsentModal(false);
    setShouldShowDropdown(search.length > 0);
  };

  const handleSearchInputChange = (_event: unknown, newInputValue: string) => {
    setSearch(cleanPhoneInput(newInputValue));
  };

  const handleContactChange = (
    _event: unknown,
    option: string | ContactsSearchContact | null,
  ) => {
    const contactId = typeof option === 'string' ? option : option?.id;

    setShouldShowDropdown(false);

    if (contacts && contactId && contacts.find(c => c.id === contactId)) {
      onSelectContact(contactId!, contacts.find(c => c.id === contactId)?.phoneNumber ?? '');
      return;
    }

    let phoneToValidate = '';
    if (typeof option === 'string') {
      phoneToValidate = option;
    } else if (option?.phoneNumber) {
      phoneToValidate = option.phoneNumber;
    }

    if (
      phoneToValidate &&
      isValidPhoneNumber(phoneToValidate) &&
      !isCreatingContact &&
      !creatingContactRef.current
    ) {
      showConsentModalForContact(phoneToValidate);
    }
  };

  const filterContactOptions = (
    options: ContactsSearchContact[],
    params: { inputValue: string },
  ) => {
    const normalizedInput = normalizePhoneNumber(params.inputValue);

    const filtered: ContactsSearchContact[] = options.filter(
      m =>
        m &&
        (m.firstName?.toUpperCase().includes(params.inputValue.toUpperCase()) ||
          m.lastName?.toUpperCase().includes(params.inputValue.toUpperCase()) ||
          m.phoneNumber?.toUpperCase().includes(params.inputValue.toUpperCase()) ||
          (m.phoneNumber && normalizePhoneNumber(m.phoneNumber).includes(normalizedInput))),
    );

    if (params.inputValue !== '' && !filtered.length && isValidPhoneNumber(params.inputValue)) {
      const normalizedForDisplay = normalizePhoneNumber(params.inputValue);
      filtered.push({
        phoneNumber: `Add +1${normalizedForDisplay}`,
        firstName: '',
        lastName: '',
        id: '',
      });
    }

    return filtered;
  };

  const handleSearchKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !isCreatingContact && !creatingContactRef.current) {
      const inputValue = search.trim();
      const cleanInputValue = cleanPhoneInput(inputValue);

      const existingContact = contacts?.find(
        c =>
          c.phoneNumber === cleanInputValue ||
          c.firstName?.toLowerCase().includes(cleanInputValue.toLowerCase()) ||
          c.lastName?.toLowerCase().includes(cleanInputValue.toLowerCase()),
      );

      if (existingContact) {
        return;
      }

      if (cleanInputValue && isValidPhoneNumber(cleanInputValue)) {
        event.preventDefault();
        showConsentModalForContact(cleanInputValue);
      }
    }
  };

  useEffect(() => {
    setContacts([]);
    setIsCreatingContact(false);
    creatingContactRef.current = false;
    setShouldShowDropdown(false);
    setShowConsentModal(false);
    setPendingContactPhone('');
  }, [selectedCompanyId]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [search]);

  useEffect(() => {
    if (!group || !selectedCompanyId) {
      return;
    }

    // Hide dropdown when starting new search
    setShouldShowDropdown(false);
    setIsContactsLoading(true);
    setContacts([]);

    const searchParams = debouncedSearch
      ? {
          query: debouncedSearch,
          companyId: selectedCompanyId,
          limit: PAGE_LIMIT,
          offset: 0,
        }
      : {
          companyId: selectedCompanyId,
          limit: PAGE_LIMIT,
          offset: 0,
        };

    let cancelled = false;

    getAllContacts(searchParams, { scope: INBOX_CONTACTS_SEARCH_SCOPE })
      .then(({ data }) => {
        if (cancelled || !data) {
          return;
        }

        const nextContacts: ContactsSearchContact[] = data.items.map(c => ({
          id: c.contact.id,
          firstName: c.contact.firstName,
          lastName: c.contact.lastName,
          phoneNumber: c.contact.phoneNumber,
        }));
        setContacts(nextContacts);
        setIsContactsLoading(false);

        // Show dropdown only after successful load and if there's search input
        if (debouncedSearch.length > 0) {
          setShouldShowDropdown(true);
        }
      })
      .catch(e => {
        if (cancelled || isRequestCanceled(e)) {
          return;
        }
        console.log(e);
        setIsContactsLoading(false);
        setShouldShowDropdown(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, selectedCompanyId, group, getAllContacts]);

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Hide dropdown when search is cleared
  useEffect(() => {
    if (search.length === 0) {
      setShouldShowDropdown(false);
    }
  }, [search]);

  return {
    searchInputRef,
    hideHeader,
    search,
    contacts,
    isContactsLoading,
    isCreatingContact,
    shouldShowDropdown,
    showConsentModal,
    handleConsentModalClose,
    createNewContact,
    handleSearchInputChange,
    handleContactChange,
    filterContactOptions,
    handleSearchKeyDown,
  };
};
